/**
 * The Vibe build-health objectives and the alarm policy judged against them —
 * one source, read by the backend's detectors and quoted by the issue
 * registry's messages, so a figure an operator reads on an alarm is the figure
 * the alarm was raised on.
 *
 * ## The two service-level objectives
 *
 * - **Queue time** — p95 of the time a build job waits for the platform under
 *   60 s. Stated as the equivalent ratio: at most 5% of build jobs wait 60 s or
 *   longer. The wait counts only while a slot of the org's own concurrency cap
 *   was free (see the backend's `measurePlatformQueueWait`): a tenant queueing
 *   behind its own builds is its cap working, and counting it would make every
 *   busy tenant breach an objective about the platform.
 * - **Infra-caused failure rate** — at most 0.5% of the build jobs that reached
 *   an outcome fail for a reason `VIBE_FAILURE_ATTRIBUTION` attributes to the
 *   platform.
 *
 * ## Why a breach is a statistical test, not a ratio over a threshold
 *
 * A tenant builds a handful of times a day. At that volume a plain ratio cries
 * wolf: one platform failure out of twenty builds is 5%, ten times the budget,
 * and is exactly what a 0.5% rate produces by chance about one day in ten. So an
 * alarm is raised only when the window's evidence REFUTES the objective — the
 * one-sided binomial probability of seeing at least that many bad build jobs, if
 * the true rate were exactly the objective, is at most `significance`. At 0.001
 * that is a false alarm roughly once in a thousand independent windows at the
 * objective itself, and rarer the healthier the platform is. What it takes, for
 * the infra objective: 2 bad of 5, 3 of 20, 5 of 100. For queue time: 4 of 5,
 * 6 of 20.
 *
 * The test decides the ALARM, never what a window is said to be. Every window
 * also carries its point estimate (`bad / judged`), and the recorded verdict
 * states it honestly: `within` only when the estimate is at or under the
 * objective's fraction, `over-budget-unconfirmed` when it is over and the test
 * has not refuted the objective, `breached` when it has. Only `breached` raises
 * an alarm, so a tenant at 3 platform failures of 46 — 6.5%, p ≈ 0.0016 against
 * 0.5% — reads over budget on its payload and on the gauges, and pages nobody.
 *
 * `minSample` is the floor below which no verdict is given at all, whatever the
 * test says: a window of one or two build jobs is not evidence about a rate.
 *
 * Two windows, each judged on its own: a day catches an incident while it is
 * happening, a week catches a sustained burn too slow to refute in a day.
 *
 * ## Starvation, which no sample size gates
 *
 * A build job still waiting on the platform, with a slot free, past
 * `starvationSeconds` is an alarm by itself. The ratio cannot see it: a queue
 * that stops moving produces no finished waits to count, and a tenant with one
 * stuck build job has a sample of one.
 *
 * ## The executor's queue, which the queue-time objective ends before
 *
 * Queue time ends when the executor ACCEPTS the attempt (`startedAt`, STARTING).
 * An accepted build can still wait — for a Nomad node that fits it, or in
 * CodeBuild's own QUEUED phase, which the executor spike measured holding 10 of
 * 24 on-demand builds in eu-west-3 for 62–152 s. So a third objective, same
 * test, same windows, same starvation shape: **executor queue time** — p95 of
 * `startedAt` → `runningAt` under 30 s.
 *
 * `runningAt` is the executor's own record of when it gave the attempt compute,
 * relayed by the tenant agent (`AgentBuildReport.runningSince`): CodeBuild's
 * PROVISIONING phase start, Nomad's allocation `CreateTime`. Never the arrival of
 * a report. `startedAt` IS a receipt time — the first report after the
 * executor accepted — so it is late by up to one agent reconcile (15 s by
 * default), and the late part moves from this objective's wait into queue
 * time's. The two waits still partition `queuedAt` → `runningAt` exactly, and a
 * `runningAt` that falls before `startedAt` is a zero wait, never a negative one.
 *
 * 30 s because the build that is not queued on the executor leaves its queue in
 * seconds on both executors, and the queued ones the spike saw sat for two to
 * five times that. `starvationSeconds` sits under CodeBuild's own queue timeout
 * (15 minutes, `QUEUED_TIMEOUT_MINUTES`), after which the executor ends the build
 * itself and the attempt is re-queued, so the alarm fires while the wait is
 * still standing.
 */
export const VIBE_BUILD_SLO_POLICY = {
  queue: {
    /** The wait an objective-meeting build job stays under. */
    objectiveSeconds: 60,
    /** p95 < objective ⇔ at most this fraction wait the objective or longer. */
    maxSlowFraction: 0.05,
    /** A build job waiting this long with a slot free is starved, whatever the sample. */
    starvationSeconds: 600
  },
  executorQueue: {
    /** The wait for compute an objective-meeting accepted build stays under. */
    objectiveSeconds: 30,
    /** p95 < objective ⇔ at most this fraction wait the objective or longer. */
    maxSlowFraction: 0.05,
    /** An accepted build still waiting for compute this long is starved, whatever the sample. */
    starvationSeconds: 600
  },
  platformFailure: {
    /** At most this fraction of outcomes may be platform failures. */
    maxFailureFraction: 0.005
  },
  /** The one-sided binomial p-value at or below which a window refutes an objective. */
  significance: 0.001,
  /** Below this many judged build jobs a window gives no verdict. */
  minSample: 5,
  windows: [
    { label: "24h", hours: 24 },
    { label: "7d", hours: 168 }
  ]
} as const;

export type VibeBuildSloWindowLabel = (typeof VIBE_BUILD_SLO_POLICY.windows)[number]["label"];

/**
 * How long a tenant's build-orphan census may report an orphan still standing
 * before it is an alarm.
 *
 * The agent itself leaves an unknown job alone for two minutes over three reads
 * before it purges one (`BUILD_ORPHAN_POLICY`), and purges a job named terminal
 * in the pass that first sees it. So a count that stays above zero for ten
 * minutes — five of those grace windows — is a purge that is failing or a
 * census that cannot vouch for itself, never a sweep still working through its
 * own grace.
 */
export const VIBE_BUILD_ORPHAN_ALARM_MINUTES = 10;

/**
 * When the git host's repository volume is too full.
 *
 * 80%, because the lever is slow and the failure is not graceful:
 *
 * - Growing the volume is a converge of the tenant's stacks, and the filesystem
 *   follows only on the node's next boot; EBS then refuses a second size change
 *   on the same volume for six hours. Hours of lead time, at best.
 * - Git needs transient space proportional to its largest repository — a repack
 *   writes the new pack before it deletes the old one — and an LFS upload lands
 *   whole. A volume at 95% can refuse a push while it still reports free space.
 * - On the 50 GiB volume every tenant starts with, 80% leaves 10 GiB: room for a
 *   repack of any repository a tenant has shipped, and for a day of pushes while
 *   the grow lands.
 *
 * `clearBelowPercent` is lower than the raise on purpose: a volume hovering at
 * 80% would otherwise raise and clear on alternate sweeps.
 */
export const VIBE_GIT_HOST_REPO_VOLUME_ALARM = {
  raiseAtPercent: 80,
  clearBelowPercent: 75
} as const;
